import { FieldProps } from 'formik'
import React from 'react'
import { Columns, Form } from 'react-bulma-components'
import FormField, { FormFieldProps } from './form-field'

/**
 * Option pour une checkbox
 */
export interface CheckboxOption {
  /** Valeur de l'option */
  value: string | number
  /** Label affiché */
  label: string
  /** Option désactivée */
  disabled?: boolean
}

/**
 * Props pour le composant CheckboxesField
 */
interface CheckboxesFieldProps extends FormFieldProps, FieldProps {
  /** Options disponibles pour les checkboxes */
  options: CheckboxOption[]
  /** Layout en colonnes (par défaut: true) */
  columns?: boolean
  /** Nombre de colonnes par ligne (si columns=true) */
  columnSize?: number | 'narrow'
}

/**
 * Composant pour gérer un groupe de checkboxes multiples
 * Gère un tableau de valeurs sélectionnées
 * S'intègre avec Formik comme les autres champs
 */
const CheckboxesField: React.FunctionComponent<CheckboxesFieldProps> = ({
  field,
  form,
  label,
  help,
  options,
  columns = true,
  columnSize = 'narrow',
  required,
  disabled = false,
  info,
}) => {
  // Valeur actuelle : tableau de valeurs sélectionnées
  const selectedValues: (string | number)[] = field?.value || []

  /**
   * Gère le changement d'une checkbox
   */
  const handleChange = (optionValue: string | number, checked: boolean) => {
    if (!field) return

    const newValues = checked
      ? [...selectedValues, optionValue]
      : selectedValues.filter(val => val !== optionValue)

    form.setFieldValue(field.name, newValues)
  }

  /**
   * Vérifie si une option est sélectionnée
   */
  const isChecked = (optionValue: string | number): boolean => {
    return selectedValues.includes(optionValue)
  }

  // Rendu des checkboxes
  const checkboxes = options.map(option => {
    const checked = isChecked(option.value)
    const optionDisabled = disabled || option.disabled

    const checkbox = (
      <Form.Checkbox
        key={option.value}
        checked={checked}
        disabled={optionDisabled}
        onChange={e => handleChange(option.value, (e.target as HTMLInputElement).checked)}
      >
        {option.label}
      </Form.Checkbox>
    )

    if (columns) {
      return (
        <Columns.Column key={option.value} size={columnSize as any}>
          {checkbox}
        </Columns.Column>
      )
    }

    return checkbox
  })

  return (
    <FormField
      label={label}
      help={help}
      error={form?.errors[field?.name] as string}
      required={required}
      disabled={disabled}
      info={info}
    >
      {columns ? <Columns multiline>{checkboxes}</Columns> : <div>{checkboxes}</div>}
    </FormField>
  )
}

export default CheckboxesField
